import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Typography, Space, Empty, Spin, Tag, Row, Col, message, Avatar, Tooltip, Table, Tabs, Collapse, Radio, Popover, DatePicker, Modal, Divider, Select } from 'antd';
import {
    ClockCircleOutlined,
    CheckCircleOutlined,
    BookOutlined,
    CalendarOutlined,
    FieldTimeOutlined,
    LeftOutlined,
    AppstoreOutlined,
    StopOutlined,
    TrophyOutlined,
    UserOutlined,
    CaretRightOutlined,
    FolderOutlined,
    BulbOutlined,
    EditOutlined,
    DeleteOutlined,
    ShareAltOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    ClockCircleFilled,
    ExclamationCircleFilled,
} from '@ant-design/icons';
import { AINestedSubject } from '../types/training';
import dayjs from 'dayjs';
import { getTrainingModules, getTrainingLeaderboard, getCurrentUserEmail } from '../services/trainingService';
import { getMoodleProgramDetails, getAiProgramProgress, getStudentModuleFeedback, setDeadlineOverride, removeDeadlineOverride, getDeadlineOverrides, sendProgressReport } from '../services/moodleService';
import { checkIsAdmin } from '../services/adminService';
import { Training, Module, TrainingStatus, LeaderboardEntry } from '../types/training';
import { parseModuleId, stripHtml, formatDisplayName } from '../utils/moduleUtils';
import ExcelJS from 'exceljs';
import { ARGB, HEX } from '../constants/reportColors';
import Leaderboard from '../components/Leaderboard';

const { Title, Text, Paragraph } = Typography;

const AILedTrainingDetails: React.FC = () => {
    const { trainingId } = useParams<{ trainingId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isAnalyzeMode = new URLSearchParams(location.search).get('mode') === 'analyze';

    const [training, setTraining] = useState<Training | null>(null);
    const [modules, setModules] = useState<Module[]>([]);
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [aiProgress, setAiProgress] = useState<any>(null);
    const [nestedHierarchy, setNestedHierarchy] = useState<AINestedSubject[] | null>(null);
    const [viewMode, setViewMode] = useState<'consolidated' | 'individual'>('consolidated');
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const [studentFeedback, setStudentFeedback] = useState<Map<number, { whyfailed: string[]; focusareas: string[] }>>(new Map());
    const [participantFeedback, setParticipantFeedback] = useState<Map<number, { whyfailed: string[]; focusareas: string[] }>>(new Map());
    // key: quizId, value: newdeadline timestamp — for admin: overrides of selected student; for participant: own overrides
    const [deadlineOverrides, setDeadlineOverrides] = useState<Record<number, number>>({});
    const [overridePicker, setOverridePicker] = useState<{ quizId: number; quizName: string } | null>(null);
    const [overrideDate, setOverrideDate] = useState<dayjs.Dayjs | null>(null);
    const [overrideSaving, setOverrideSaving] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [stakeholderEmails, setStakeholderEmails] = useState<string[]>([]);

    const deadlinePassed = training?.endDate
        ? dayjs(training.endDate.toString().split('T')[0]).isBefore(dayjs(), 'day')
        : false;

    // Quiz IDs that are actually visible to participants (mapped in the hierarchy JSON).
    // Orphaned Moodle quizzes (created by re-save race condition, not in the hierarchy) are excluded.
    // null = no hierarchy (flat program) → all scores count.
    const validHierarchyQuizIds: Set<number> | null = useMemo(() => {
        if (!nestedHierarchy || !aiProgress?.modules) return null;
        const assigned = new Set<number>();
        for (const subject of nestedHierarchy) {
            for (const chapter of (subject.chapters || [])) {
                for (const hMod of (chapter.modules || [])) {
                    const directId = Number((hMod as any).moodleId) || 0;
                    let quizId = directId;
                    if (!quizId || assigned.has(quizId)) {
                        const cleanName = parseModuleId(hMod.name).name;
                        const match = (aiProgress.modules as any[]).find(
                            (m: any) => parseModuleId(m.name).name === cleanName && !assigned.has(m.id)
                        );
                        quizId = match?.id || 0;
                    }
                    if (quizId) assigned.add(quizId);
                }
            }
        }
        return assigned;
    }, [nestedHierarchy, aiProgress]);

    // Avg score over hierarchy-mapped attempted quizzes only (excludes orphaned + not-started).
    // Returns -1 when no quizzes have been attempted.
    const computeParticipantAvgScore = (p: any): number => {
        const allScores: any[] = p.quizscores || [];
        const scores = validHierarchyQuizIds
            ? allScores.filter((qs: any) => validHierarchyQuizIds.has(qs.quizid))
            : allScores;
        const attempted = scores.filter((qs: any) => qs.status === 'Passed' || qs.status === 'Failed');
        if (attempted.length === 0) return -1;
        return attempted.reduce((sum: number, qs: any) => sum + (qs.grade || 0), 0) / attempted.length;
    };

    const computeParticipantStatus = (p: any): string => {
        const allScores: any[] = p.quizscores || [];
        // Exclude orphaned quizzes (not in hierarchy) from status computation
        const scores = validHierarchyQuizIds
            ? allScores.filter((qs: any) => validHierarchyQuizIds.has(qs.quizid))
            : allScores;
        const total = scores.length;
        if (total === 0) return 'Not Started';
        if (!deadlinePassed && training?.active !== false) return 'In Progress';
        const notStarted = scores.filter((qs: any) => qs.status === 'Not Started').length;
        if (notStarted === total) return 'Not Started';
        if (notStarted > 0) return 'Incomplete';
        // All quizzes attempted — judge by program-level passing percentage
        const passingPct = training?.passingPercentage ?? 80;
        return computeParticipantAvgScore(p) >= passingPct ? 'Passed' : 'Failed';
    };
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        if (!trainingId) return;
        // Run training load, admin check, and user email fetch in parallel.
        // All three are needed before making enrollment/access decisions.
        Promise.all([loadTrainingDetails(), checkIsAdmin(), getCurrentUserEmail()])
            .then(([trainingData, adminStatus, userEmail]) => {
                setIsAdmin(adminStatus);

                // Enrollment guard: non-admins cannot view PRIVATE/REQUESTABLE
                // programs unless their email is in the audience list.
                if (!adminStatus && trainingData && trainingData.trainingType !== 'Open') {
                    const audience = (trainingData.audienceEmails || '')
                        .split(/[;,\n]+/)
                        .map((e: string) => e.trim().toLowerCase())
                        .filter(Boolean);
                    if (userEmail && !audience.includes(userEmail.toLowerCase())) {
                        navigate('/', { replace: true });
                        return;
                    }
                }

                // Analyze mode: only admins may see participant data.
                // Non-admins are redirected to the plain URL before any data is fetched.
                if (isAnalyzeMode && adminStatus) {
                    fetchAiProgress();
                } else if (isAnalyzeMode && !adminStatus) {
                    navigate(`/training/${trainingId}`, { replace: true });
                }
            });
        // isAnalyzeMode intentionally excluded: navigate() changes the URL which
        // re-renders with isAnalyzeMode=false, avoiding a second redirect loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainingId]);

    useEffect(() => {
        if (!training?.courseId) return;
        getCurrentUserEmail().then(email => {
            getStudentModuleFeedback(email, training.courseId as number)
                .then(list => {
                    const map = new Map<number, { whyfailed: string[]; focusareas: string[] }>();
                    list.forEach(fb => map.set(fb.quizid, { whyfailed: fb.whyfailed, focusareas: fb.focusareas }));
                    setParticipantFeedback(map);
                })
                .catch(() => {});
        }).catch(() => {});
    }, [training?.courseId]);

    // Load deadline overrides whenever the selected student changes (admin view)
    useEffect(() => {
        if (!selectedStudent?.email || !trainingId || !isAdmin) return;
        getDeadlineOverrides(trainingId, selectedStudent.email)
            .then(list => {
                const map: Record<number, number> = {};
                list.forEach(o => { if (o.newdeadline > 0) map[o.quizid] = o.newdeadline; });
                setDeadlineOverrides(map);
            })
            .catch(() => {});
    }, [selectedStudent?.email, trainingId, isAdmin]);

    // Load own deadline overrides for participant view
    useEffect(() => {
        if (isAdmin || !trainingId) return;
        getCurrentUserEmail().then(email => {
            getDeadlineOverrides(trainingId, email)
                .then(list => {
                    const map: Record<number, number> = {};
                    list.forEach(o => { if (o.newdeadline > 0) map[o.quizid] = o.newdeadline; });
                    setDeadlineOverrides(map);
                })
                .catch(() => {});
        }).catch(() => {});
    }, [trainingId, isAdmin]);

    useEffect(() => {
        if (!selectedStudent?.email || !training?.courseId) return;
        setStudentFeedback(new Map());
        getStudentModuleFeedback(selectedStudent.email, training.courseId as number)
            .then(list => {
                const map = new Map<number, { whyfailed: string[]; focusareas: string[] }>();
                list.forEach(fb => map.set(fb.quizid, { whyfailed: fb.whyfailed, focusareas: fb.focusareas }));
                setStudentFeedback(map);
            })
            .catch(() => {});
    }, [selectedStudent?.email, training?.courseId]);

    const fetchAiProgress = async () => {
        try {
            const data = await getAiProgramProgress(trainingId as string);
            setAiProgress(data);
        } catch (error) {
            console.error('Error fetching AI program progress:', error);
        }
    };

    const loadTrainingDetails = async (): Promise<Training | null> => {
        setLoading(true);
        try {
            // Fire all three independently - none actually depends on another.
            // trainingId (the route param) is already known before any of them
            // run; getTrainingModules/getTrainingLeaderboard never needed
            // anything getMoodleProgramDetails returns, they were just reusing
            // its echoed-back trainingId field. Promise.allSettled (not
            // Promise.all) so a bad trainingId failing all three doesn't
            // produce an unhandled rejection - the failure handling below is
            // gated specifically on each individual result.
            const [detailsResult, modulesResult, leaderboardResult] = await Promise.allSettled([
                getMoodleProgramDetails(trainingId as string),
                getTrainingModules(trainingId as string),
                getTrainingLeaderboard(trainingId as string),
            ]);

            if (detailsResult.status === 'rejected') {
                console.error('Error loading AI training details:', detailsResult.reason);
                message.error('Failed to load training details');
                return null;
            }

            const currentTraining = detailsResult.value;
            if (!currentTraining) {
                message.error('Training not found');
                navigate('/');
                return null;
            }
            setTraining(currentTraining);
            // Detect nested hierarchy
            try {
                const parsed = JSON.parse((currentTraining as any).assessments || '');
                if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].chapters !== undefined) {
                    setNestedHierarchy(parsed as AINestedSubject[]);
                }
            } catch {}

            if (modulesResult.status === 'fulfilled') {
                setModules(modulesResult.value);
            } else {
                console.error('Error loading AI training details:', modulesResult.reason);
                message.error('Failed to load training details');
                return null;
            }

            if (leaderboardResult.status === 'fulfilled') {
                setLeaderboard(leaderboardResult.value);
            } else {
                console.warn('Failed to load leaderboard:', leaderboardResult.reason);
            }
            return currentTraining;
        } finally {
            setLoading(false);
        }
    };

    const handleStartAssessment = (moduleId: string, quizId: number = 0): void => {
        if (training) {
            const cleanId = moduleId.includes('[') ? moduleId.split('[')[0].trim() : moduleId.trim();
            const quizParam = quizId > 0 ? `&quizId=${quizId}` : '';
            navigate(`/assessment/${encodeURIComponent(training.trainingId)}?module=${encodeURIComponent(cleanId)}${quizParam}`);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;
    if (!training) return <Empty description="Training not found"><Button type="primary" onClick={() => navigate('/')}>Back</Button></Empty>;

    const isDeactivated = !training.active;
    const isEvergreen = training.trainingType === 'Open' && (!training.startDate || training.startDate === '');
    const hasStarted = isEvergreen || (training.startDate !== '' && (dayjs().isSame(dayjs(training.startDate.split('T')[0]).startOf('day')) || dayjs().isAfter(dayjs(training.startDate.split('T')[0]).startOf('day'))));
    const isExpired = !isEvergreen && training.endDate ? dayjs().isAfter(dayjs(training.endDate.split('T')[0]).endOf('day')) : false;
    const active = !isDeactivated && !isExpired && hasStarted;

    const drillIn = (record: any) => {
        setSelectedStudent(record);
        setViewMode('individual');
    };

    // Flatten quiz columns in hierarchy order (Subject › Chapter › Quiz), or fall back to flat modules.
    // Returns { name, quizId, group } for each quiz column.
    const getAiReportCols = (): { name: string; quizId: number; group: string }[] => {
        if (nestedHierarchy && aiProgress?.modules) {
            const cols: { name: string; quizId: number; group: string }[] = [];
            const assignedIds = new Set<number>();
            for (const subject of nestedHierarchy) {
                for (const chapter of (subject.chapters || [])) {
                    for (const hMod of (chapter.modules || [])) {
                        const directId = Number((hMod as any).moodleId) || 0;
                        let quizId = directId;
                        if (!quizId || assignedIds.has(quizId)) {
                            // Name-based fallback: find first unassigned quiz matching this name
                            const cleanName = parseModuleId(hMod.name).name;
                            const match = (aiProgress?.modules || []).find(
                                (m: any) => parseModuleId(m.name).name === cleanName && !assignedIds.has(m.id)
                            );
                            quizId = match?.id || 0;
                        }
                        if (quizId) assignedIds.add(quizId);
                        cols.push({ name: parseModuleId(hMod.name).name, quizId, group: `${subject.name} › ${chapter.name}` });
                    }
                }
            }
            return cols;
        }
        return (aiProgress?.modules || []).map((mod: any) => ({
            name: parseModuleId(mod.name).name,
            quizId: mod.id,
            group: '',
        }));
    };

    const buildAiReportBuffer = async (): Promise<ArrayBuffer> => {
        const cols = getAiReportCols();
        const participants: any[] = aiProgress?.participants || [];
        const programName = training!.trainingName;
        const period = training!.startDate && training!.endDate
            ? `${dayjs(training!.startDate.toString().split('T')[0]).format('DD MMM YYYY')} – ${dayjs(training!.endDate.toString().split('T')[0]).format('DD MMM YYYY')}`
            : 'Evergreen Program';

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Progress Report');

        const totalCols = 2 + cols.length + 2;
        ws.columns = [
            { width: 26 }, { width: 32 },
            ...cols.map(() => ({ width: 22 })),
            { width: 14 }, { width: 16 },
        ];

        const titleRow = ws.addRow([programName]);
        ws.mergeCells(1, 1, 1, totalCols);
        titleRow.height = 32;
        const titleCell = titleRow.getCell(1);
        titleCell.font = { bold: true, size: 14, color: { argb: ARGB.titleText } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.titleBg } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

        const summaryRow = ws.addRow([`Period: ${period}   |   Participants: ${participants.length}   |   Passing Cutoff: ${training!.passingPercentage}%`]);
        ws.mergeCells(2, 1, 2, totalCols);
        summaryRow.height = 20;
        const sumCell = summaryRow.getCell(1);
        sumCell.font = { size: 11, color: { argb: ARGB.summaryText } };
        sumCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.summaryBg } };
        sumCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

        const hasGroups = cols.some(c => !!c.group);
        sumCell.border = { bottom: { style: 'thin', color: { argb: hasGroups ? ARGB.groupHeaderBg : ARGB.headerBg } } };

        const hdrBorder = { style: 'thin' as const, color: { argb: ARGB.headerBorder } };
        const fullHdrBorder = { top: hdrBorder, bottom: hdrBorder, left: hdrBorder, right: hdrBorder };
        const dataBorder = { style: 'thin' as const, color: { argb: ARGB.rowBorder } };
        const fullDataBorder = { top: dataBorder, bottom: dataBorder, left: dataBorder, right: dataBorder };

        const styleFixed = (cell: any, value: string, align: 'left' | 'center' = 'center') => {
            cell.value = value;
            cell.font = { bold: true, color: { argb: ARGB.headerText }, size: 10 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.headerBg } };
            cell.alignment = { wrapText: true, vertical: 'middle', horizontal: align };
            cell.border = fullHdrBorder;
        };

        if (hasGroups) {
            // Add BOTH rows before setting any merges so row 4 exists when merges are applied
            const grpRow = ws.addRow(new Array(totalCols).fill(''));
            grpRow.height = 26;
            const asgRow = ws.addRow(new Array(totalCols).fill(''));
            asgRow.height = 26;

            // Build ordered group list (preserve column order)
            const groupOrder: string[] = [];
            const groupSizes: Record<string, number> = {};
            for (const c of cols) {
                const g = c.group || '';
                if (!groupSizes[g]) { groupOrder.push(g); groupSizes[g] = 0; }
                groupSizes[g]++;
            }

            // Set all merges first
            ws.mergeCells(3, 1, 4, 1);
            ws.mergeCells(3, 2, 4, 2);
            ws.mergeCells(3, totalCols - 1, 4, totalCols - 1);
            ws.mergeCells(3, totalCols, 4, totalCols);
            let colOffset = 3;
            for (const g of groupOrder) {
                const size = groupSizes[g];
                if (size > 1) ws.mergeCells(3, colOffset, 3, colOffset + size - 1);
                colOffset += size;
            }

            // Style row 3 (group headers)
            styleFixed(grpRow.getCell(1), 'Name', 'left');
            styleFixed(grpRow.getCell(2), 'Email', 'left');
            styleFixed(grpRow.getCell(totalCols - 1), 'Avg Score');
            styleFixed(grpRow.getCell(totalCols), 'Status');
            let grpOffset = 3;
            for (const g of groupOrder) {
                const cell = grpRow.getCell(grpOffset);
                cell.value = g;
                cell.font = { bold: true, color: { argb: ARGB.groupHeaderText }, size: 10 };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.groupHeaderBg } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = fullHdrBorder;
                grpOffset += groupSizes[g];
            }

            // Style row 4 (quiz name sub-headers)
            cols.forEach((c, idx) => {
                const cell = asgRow.getCell(3 + idx);
                cell.value = c.name;
                cell.font = { bold: true, color: { argb: ARGB.subHeaderText }, size: 10 };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.subHeaderBg } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = fullHdrBorder;
            });

            ws.views = [{ state: 'frozen', xSplit: 2, ySplit: 4 }];
        } else {
            // Single header row (flat program, no Subject/Chapter hierarchy)
            const headerRow = ws.addRow(['Name', 'Email', ...cols.map(c => c.name), 'Avg Score', 'Status']);
            headerRow.height = 44;
            headerRow.eachCell((cell, colIdx) => {
                cell.font = { bold: true, color: { argb: ARGB.headerText }, size: 10 };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.headerBg } };
                cell.alignment = { wrapText: true, vertical: 'middle', horizontal: colIdx <= 2 ? 'left' : 'center' };
                cell.border = fullHdrBorder;
            });
            ws.views = [{ state: 'frozen', xSplit: 2, ySplit: 3 }];
        }

        for (const p of participants) {
            const scoreByQuizId: Record<number, any> = {};
            for (const qs of (p.quizscores || [])) scoreByQuizId[qs.quizid] = qs;

            const cellTexts: string[] = [];
            const cellArgb: string[] = [];
            const cellFgArgb: string[] = [];

            for (const col of cols) {
                const qs = col.quizId ? scoreByQuizId[col.quizId] : null;
                if (!qs || qs.status === 'Not Started') {
                    cellTexts.push('—');
                    cellArgb.push(ARGB.notStartedBg);
                    cellFgArgb.push(ARGB.notStartedText);
                } else {
                    cellTexts.push(`${qs.status}\n${Math.round(qs.grade)}% · ${qs.attempts} att`);
                    cellArgb.push(qs.status === 'Passed' ? ARGB.passedBg : ARGB.failedBg);
                    cellFgArgb.push(qs.status === 'Passed' ? ARGB.passedText : ARGB.failedText);
                }
            }

            const avgScore = computeParticipantAvgScore(p);
            const avgStr = avgScore >= 0 ? `${avgScore.toFixed(1)}%` : '—';
            const avgArgb = avgScore >= 75 ? ARGB.scoreGood : avgScore >= 50 ? ARGB.scoreWarn : avgScore >= 0 ? ARGB.scoreBad : ARGB.scoreNone;
            const _eps = computeParticipantStatus(p);
            const statusArgb = _eps === 'Passed' ? ARGB.scoreGood : _eps === 'Failed' ? ARGB.scoreBad : _eps === 'In Progress' ? ARGB.scoreWarn : _eps === 'Incomplete' ? ARGB.scoreWarn : ARGB.scoreNone;

            const dataRow = ws.addRow([p.fullname || '', p.email || '', ...cellTexts, avgStr, _eps]);
            dataRow.height = 40;
            dataRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.rowBg } };
            dataRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.rowBg } };
            cellTexts.forEach((_, idx) => {
                const cell = dataRow.getCell(3 + idx);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: cellArgb[idx] } };
                cell.font = { size: 10, color: { argb: cellFgArgb[idx] } };
                cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
            });
            const lastFixed = 2 + cols.length;
            dataRow.getCell(lastFixed + 1).font = { bold: true, color: { argb: avgArgb } };
            dataRow.getCell(lastFixed + 1).alignment = { horizontal: 'center' };
            dataRow.getCell(lastFixed + 2).font = { color: { argb: statusArgb } };
            dataRow.getCell(lastFixed + 2).alignment = { horizontal: 'center' };
            dataRow.eachCell(cell => { cell.border = fullDataBorder; });
        }

        return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>;
    };

    const handleShareAiReport = async () => {
        if (!stakeholderEmails.length || !training?.courseId) return;
        setSharing(true);
        try {
            const buffer = await buildAiReportBuffer();
            const uint8 = new Uint8Array(buffer);
            let b64 = '';
            const chunk = 6144; // must be divisible by 3 to avoid mid-string base64 padding
            for (let i = 0; i < uint8.length; i += chunk) {
                b64 += btoa(String.fromCharCode(...Array.from(uint8.subarray(i, i + chunk))));
            }
            const participants: any[] = aiProgress?.participants || [];
            const passedCount = participants.filter((p: any) => computeParticipantStatus(p) === 'Completed').length;
            const overallPct = participants.length > 0 ? Math.round((passedCount / participants.length) * 100) : 0;
            const period = training!.startDate && training!.endDate
                ? `${dayjs(training!.startDate.toString().split('T')[0]).format('DD MMM YYYY')} – ${dayjs(training!.endDate.toString().split('T')[0]).format('DD MMM YYYY')}`
                : 'Evergreen Program';
            const result = await sendProgressReport({
                courseid: training!.courseId,
                stakeholder_emails: JSON.stringify(stakeholderEmails),
                attachment_b64: b64,
                attachment_filename: `Progress_Report_${training!.trainingName.replace(/[^a-z0-9]/gi, '_')}.xlsx`,
                program_name: training!.trainingName,
                period,
                participant_count: participants.length,
                overall_pct: overallPct,
            });
            message.success(`Report sent to ${result.sent} recipient${result.sent !== 1 ? 's' : ''}`);
            setShareModalOpen(false);
            setStakeholderEmails([]);
        } catch {
            message.error('Failed to send report. Please try again.');
        } finally {
            setSharing(false);
        }
    };

    const sortedParticipants: any[] = (() => {
        const participants = aiProgress?.participants || [];
        return [...participants]
            .map((p: any) => {
                return { ...p, _avgscore: computeParticipantAvgScore(p) };
            })
            .sort((a: any, b: any) => b._avgscore - a._avgscore);
    })();

    const makeQuizColumn = (name: string, quizId: number, colKey?: string) => {
        const shortName = name.length > 18 ? name.slice(0, 18) + '…' : name;
        return {
            title: <Tooltip title={name}><span style={{ fontSize: 12 }}>{shortName}</span></Tooltip>,
            key: colKey || `quiz_${quizId}`,
            align: 'center' as const,
            width: 110,
            render: (_: any, record: any) => {
                let qs: any;
                if (quizId) {
                    // Primary: exact quiz ID match — used for all programs that store moodleId.
                    qs = (record.quizscores || []).find((q: any) => q.quizid === quizId);
                } else {
                    // Legacy fallback: moodleId was not stored when this program was created.
                    // Match by cleaned name against aiProgress.modules to find candidate quiz IDs,
                    // then pick the max grade among all matches. Max is used because duplicate
                    // names are possible and we prefer showing the best attempt over hiding data.
                    const cleanName = parseModuleId(name).name;
                    const candidateIds = new Set(
                        (aiProgress?.modules || [])
                            .filter((m: any) => parseModuleId(m.name).name === cleanName)
                            .map((m: any) => m.id)
                    );
                    // Only consider attempted entries — avoids surfacing a score from a
                    // different same-named quiz that the participant completed elsewhere.
                    const attempted = (record.quizscores || []).filter((q: any) => candidateIds.has(q.quizid) && q.status !== 'Not Started');
                    qs = attempted.reduce((best: any, cur: any) => (!best || cur.grade > best.grade ? cur : best), null);
                }
                if (!qs || qs.status === 'Not Started') return <Text type="secondary" style={{ fontSize: 13 }}>—</Text>;
                const color = qs.status === 'Passed' ? '#52c41a' : qs.status === 'Failed' ? '#ff4d4f' : '#fa8c16';
                return (
                    <Tooltip title={`${qs.status} · ${qs.attempts} attempt${qs.attempts !== 1 ? 's' : ''}`}>
                        <div>
                            <div style={{ fontWeight: 600, color, fontSize: 14 }}>{Math.round(qs.grade)}%</div>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{qs.attempts} att</div>
                        </div>
                    </Tooltip>
                );
            },
        };
    };

    const progressColumns: any[] = nestedHierarchy && aiProgress?.modules
        ? nestedHierarchy.map((subject: any) => ({
            title: <span style={{ fontWeight: 600, fontSize: 13 }}>{subject.name}</span>,
            key: `subj_${subject.id}`,
            children: (subject.chapters || []).map((chapter: any) => ({
                title: <span style={{ fontSize: 12 }}>{chapter.name}</span>,
                key: `chap_${chapter.id}`,
                children: (chapter.modules || []).map((hMod: any) => {
                    const quizId = Number(hMod.moodleId) || 0;
                    return makeQuizColumn(hMod.name, quizId, `hmod_${hMod.id}`);
                }),
            })).filter((c: any) => c.children && c.children.length > 0),
        })).filter((s: any) => s.children && s.children.length > 0)
        : (aiProgress?.modules || []).map((mod: any) => {
            const { name: displayName } = parseModuleId(mod.name);
            return makeQuizColumn(displayName, mod.id);
        });

    return (
        <div style={{ padding: '24px' }}>
            <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0', opacity: isDeactivated ? 0.8 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'start' }}>
                    <Button
                        icon={<LeftOutlined />}
                        onClick={() => navigate(isAnalyzeMode ? '/admin' : '/')}
                        style={{ flexShrink: 0, marginRight: 16, marginTop: 4 }}
                    />
                    <div style={{
                        width: 80, height: 80, borderRadius: '16px',
                        backgroundColor: '#fff7e6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginRight: 24
                    }}>
                        <AppstoreOutlined style={{ fontSize: '32px', color: '#fa541c' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Space size={12} style={{ marginBottom: 4 }}>
                            {isDeactivated && <Tag color="red">Closed</Tag>}
                            {!isDeactivated && isExpired && <Tag color="warning">Expired</Tag>}
                            <Tag color="blue">AI Led</Tag>
                            <Text type="secondary">ID: {training.trainingId}</Text>
                        </Space>
                        <Title level={2} style={{ margin: '0 0 16px 0' }}>{training.trainingName}</Title>

                        <Space size={24} wrap>
                            <Text type="secondary">
                                <CalendarOutlined style={{ marginRight: 8 }} />
                                {training.startDate && training.endDate
                                    ? `${dayjs(training.startDate.split('T')[0]).format('DD/MM/YYYY')} - ${dayjs(training.endDate.split('T')[0]).format('DD/MM/YYYY')}`
                                    : 'Evergreen Program'
                                }
                            </Text>
                            {training.estimatedEffortHours && <Text type="secondary"><FieldTimeOutlined style={{ marginRight: 8 }} /> Duration: {training.estimatedEffortHours} hrs</Text>}
                            <Text type="secondary"><TrophyOutlined style={{ marginRight: 8 }} /> Passing Cutoff: {training.passingPercentage}%</Text>
                        </Space>

                        {(() => {
                            const raw = (training.trainingLink || '').trim();
                            if (!raw || raw.toLowerCase() === 'n/a') return null;
                            // Auto-linkify plain-text URLs, then render as HTML so admins can also write <a> tags directly
                            const html = raw
                                .replace(/(https?:\/\/[^\s<>"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
                                .replace(/\n/g, '<br/>');
                            return (
                                <div style={{ marginTop: 16 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 8 }}><BookOutlined style={{ marginRight: 8 }} /> Resource Links</Text>
                                    <div
                                        dangerouslySetInnerHTML={{ __html: html }}
                                        style={{ fontSize: 13, lineHeight: '1.8', color: '#595959' }}
                                    />
                                </div>
                            );
                        })()}

                    </div>
                    {isAdmin && isAnalyzeMode && (
                        <Button type="primary" icon={<ShareAltOutlined />} style={{ flexShrink: 0, marginLeft: 16, marginTop: 4 }} onClick={() => setShareModalOpen(true)} disabled={!aiProgress?.participants?.length}>
                            Share Report
                        </Button>
                    )}
                </div>
            </Card>

            {isAdmin && isAnalyzeMode ? (
                <>
                    <div style={{ marginBottom: '12px' }}>
                        <Radio.Group
                            value={viewMode}
                            onChange={e => {
                                const val = e.target.value;
                                setViewMode(val);
                                if (val === 'individual' && !selectedStudent && aiProgress?.participants?.length > 0) {
                                    setSelectedStudent(aiProgress.participants[0]);
                                }
                            }}
                            buttonStyle="solid"
                            size="large"
                        >
                            <Radio.Button value="consolidated">Consolidated Progress</Radio.Button>
                            <Radio.Button value="individual">Individual View</Radio.Button>
                        </Radio.Group>
                    </div>

                    {viewMode === 'consolidated' ? (
                        <Card style={{ borderRadius: '12px', marginTop: '8px' }} title="Student Progress">
                            <Table
                                dataSource={sortedParticipants}
                                rowKey="email"
                                loading={!aiProgress}
                                scroll={{ x: 'max-content' }}
                                size="small"
                                bordered
                                pagination={{ pageSize: 20, showSizeChanger: false }}
                                columns={[
                                    {
                                        title: '#',
                                        key: 'rank',
                                        fixed: 'left' as const,
                                        width: 48,
                                        render: (_: any, __: any, index: number) => {
                                            const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                                            return medal
                                                ? <span style={{ fontSize: 18 }}>{medal}</span>
                                                : <Text type="secondary" style={{ fontSize: 13 }}>#{index + 1}</Text>;
                                        },
                                    },
                                    {
                                        title: 'Student',
                                        key: 'student',
                                        fixed: 'left' as const,
                                        width: 220,
                                        render: (_: any, record: any) => (
                                            <Space>
                                                <Avatar icon={<UserOutlined />} src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatDisplayName(record.fullname, record.email) || 'U')}&background=random`} />
                                                <div>
                                                    <div style={{ fontWeight: 'bold' }}>{formatDisplayName(record.fullname, record.email) || 'Unknown'}</div>
                                                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.email || ''}</div>
                                                </div>
                                            </Space>
                                        ),
                                    },
                                    ...progressColumns,
                                    {
                                        title: 'Avg Score',
                                        key: 'avgscore',
                                        fixed: 'right' as const,
                                        width: 100,
                                        render: (_: any, record: any) => {
                                            const avg = record._avgscore;
                                            if (avg < 0) return <Text type="secondary">—</Text>;
                                            const color = avg >= 75 ? '#52c41a' : avg >= 50 ? '#fa8c16' : '#ff4d4f';
                                            return (
                                                <Text strong style={{ fontSize: 15, color }}>
                                                    {avg.toFixed(1)}%
                                                </Text>
                                            );
                                        },
                                    },
                                    {
                                        title: 'Overall',
                                        key: 'overall',
                                        fixed: 'right' as const,
                                        width: 120,
                                        render: (_: any, record: any) => {
                                            const s = computeParticipantStatus(record);
                                            if (s === 'Passed') return <Tag color="success">Passed</Tag>;
                                            if (s === 'Failed') return <Tag color="error">Failed</Tag>;
                                            if (s === 'In Progress') return <Tag color="processing">In Progress</Tag>;
                                            if (s === 'Incomplete') return <Tag color="warning">Incomplete</Tag>;
                                            return <Tag color="default">Not Started</Tag>;
                                        },
                                    },
                                    {
                                        title: '',
                                        key: 'action',
                                        fixed: 'right' as const,
                                        width: 90,
                                        render: (_: any, record: any) => (
                                            <Button size="small" type="link" onClick={() => drillIn(record)}>Details</Button>
                                        )
                                    }
                                ]}
                            />
                        </Card>
                    ) : (
                        <>
                        {selectedStudent && (() => {
                            const studentData = (aiProgress?.participants || []).find((p: any) => p.email === selectedStudent.email);
                            const allMods = (aiProgress?.modules || []).filter((m: any) =>
                                !validHierarchyQuizIds || validHierarchyQuizIds.has(m.id)
                            );
                            if (!studentData || !allMods.length) return null;
                            const quizScores = studentData.quizscores || [];
                            const passed = allMods.filter((m: any) => quizScores.find((q: any) => q.quizid === m.id)?.status === 'Passed').length;
                            const pct = allMods.length > 0 ? Math.round((passed / allMods.length) * 100) : 0;
                            return (
                                <div style={{ margin: '0 0 8px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                                    <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto' }}>
                                        <div style={{ flexShrink: 0, padding: '6px 12px', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: '#fff' }}>
                                            <div style={{ fontSize: 16, fontWeight: 700, color: pct >= 70 ? '#389e0d' : pct >= 40 ? '#d46b08' : '#8c8c8c', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{pct}%</div>
                                            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2, whiteSpace: 'nowrap' }}>{passed}/{allMods.length} passed</div>
                                        </div>
                                        {allMods.map((m: any, i: number) => {
                                            const qs = quizScores.find((q: any) => q.quizid === m.id);
                                            const status = qs?.status || 'Not Started';
                                            const isPassed = status === 'Passed';
                                            const isFailed = status === 'Failed';
                                            const isInProgress = status === 'In Progress';
                                            const grade = qs?.grade != null ? Math.round(qs.grade) : null;
                                            return (
                                                <Tooltip key={m.id} title={
                                                    <span style={{ fontSize: 11 }}>
                                                        {parseModuleId(m.name).name}<br />
                                                        {status}{grade != null ? ` · ${grade}%` : ''}
                                                        {qs?.attempts ? ` · ${qs.attempts} attempt${qs.attempts !== 1 ? 's' : ''}` : ''}
                                                    </span>
                                                }>
                                                    <div style={{
                                                        flexShrink: 0, minWidth: 68, padding: '5px 6px', textAlign: 'center',
                                                        borderRight: i < allMods.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                        background: isPassed ? '#f6ffed' : isFailed ? '#fff1f0' : isInProgress ? '#fff7e6' : 'transparent',
                                                        cursor: 'default',
                                                    }}>
                                                        <div style={{ fontSize: 9, color: '#8c8c8c', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 64 }}>{parseModuleId(m.name).name}</div>
                                                        {isPassed
                                                            ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 15 }} />
                                                            : isFailed
                                                                ? <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 15 }} />
                                                                : isInProgress
                                                                    ? <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 15 }} />
                                                                    : <ClockCircleFilled style={{ color: '#d9d9d9', fontSize: 15 }} />
                                                        }
                                                        {grade != null && <div style={{ fontSize: 9, color: isPassed ? '#52c41a' : isFailed ? '#ff4d4f' : '#8c8c8c', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{grade}%</div>}
                                                    </div>
                                                </Tooltip>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                        <Row gutter={16} style={{ marginTop: '8px' }}>
                            <Col span={7}>
                                <Card size="small" title="Students" style={{ borderRadius: 12 }} bodyStyle={{ padding: '8px' }}>
                                    {!aiProgress ? (
                                        <div style={{ textAlign: 'center', padding: '20px 0' }}><Spin /></div>
                                    ) : (aiProgress?.participants || []).length === 0 ? (
                                        <Empty description="No participants yet" />
                                    ) : (
                                        (aiProgress?.participants || []).map((p: any) => {
                                            const isSelected = selectedStudent?.email === p.email;
                                            const _ps = computeParticipantStatus(p);
                                            const statusColor = _ps === 'Passed' ? 'success' : _ps === 'Failed' ? 'error' : _ps === 'In Progress' ? 'processing' : _ps === 'Incomplete' ? 'warning' : 'default';
                                            return (
                                                <div
                                                    key={p.email}
                                                    onClick={() => setSelectedStudent(p)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        padding: '8px 10px',
                                                        background: isSelected ? '#e6f4ff' : 'transparent',
                                                        borderRadius: 6,
                                                        marginBottom: 4,
                                                        border: isSelected ? '1px solid #91caff' : '1px solid transparent',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar size="small" icon={<UserOutlined />} src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatDisplayName(p.fullname, p.email) || 'U')}&background=random`} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: isSelected ? 600 : 400, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDisplayName(p.fullname, p.email)}</div>
                                                            <Tag color={statusColor} style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{_ps}</Tag>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </Card>
                            </Col>
                            <Col span={17}>
                                {!selectedStudent ? (
                                    <Empty description="Select a student from the list to view their quiz scores" style={{ padding: '60px 0' }} />
                                ) : (() => {
                                    const studentData = (aiProgress?.participants || []).find((p: any) => p.email === selectedStudent.email);
                                    if (!studentData) return <Empty description="No data for this student" />;
                                    const _sps = computeParticipantStatus(studentData);
                                    const statusColor = _sps === 'Passed' ? 'success' : _sps === 'Failed' ? 'error' : _sps === 'In Progress' ? 'processing' : _sps === 'Incomplete' ? 'warning' : 'default';

                                    const modExpandable = {
                                        expandedRowRender: (record: any) => {
                                            const fb = studentFeedback.get(record.id);
                                            if (!fb || (!fb.whyfailed.length && !fb.focusareas.length)) {
                                                return <Text type="secondary" style={{ fontSize: 12 }}>No AI feedback available for this module yet.</Text>;
                                            }
                                            return (
                                                <Row gutter={24} style={{ padding: '4px 8px' }}>
                                                    <Col span={12}>
                                                        <Text strong style={{ color: '#ff4d4f', fontSize: 12 }}>Why Struggled</Text>
                                                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                                                            {fb.whyfailed.map((b: string, i: number) => <li key={i} style={{ fontSize: 12, color: '#595959', marginBottom: 2 }}>{b}</li>)}
                                                        </ul>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Text strong style={{ color: '#1677ff', fontSize: 12 }}>Focus Areas</Text>
                                                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                                                            {fb.focusareas.map((b: string, i: number) => <li key={i} style={{ fontSize: 12, color: '#595959', marginBottom: 2 }}>{b}</li>)}
                                                        </ul>
                                                    </Col>
                                                </Row>
                                            );
                                        },
                                        rowExpandable: (record: any) => !!record.qs && record.qs.status !== 'Not Started',
                                    };

                                    const modColumns = [
                                        {
                                            title: 'Module',
                                            dataIndex: 'name',
                                            key: 'name',
                                            render: (name: string) => <Text>{parseModuleId(name).name}</Text>,
                                        },
                                        {
                                            title: 'Score',
                                            key: 'score',
                                            width: 90,
                                            align: 'center' as const,
                                            render: (_: any, record: any) => {
                                                const qs = record.qs;
                                                if (!qs || qs.status === 'Not Started') return <Text type="secondary">—</Text>;
                                                const color = qs.status === 'Passed' ? '#52c41a' : '#ff4d4f';
                                                return <Text strong style={{ color }}>{Math.round(qs.grade)}%</Text>;
                                            },
                                        },
                                        {
                                            title: 'Attempts',
                                            key: 'attempts',
                                            width: 90,
                                            align: 'center' as const,
                                            render: (_: any, record: any) => (
                                                <Text type={record.qs?.attempts > 0 ? undefined : 'secondary'}>{record.qs?.attempts || '—'}</Text>
                                            ),
                                        },
                                        {
                                            title: 'Status',
                                            key: 'status',
                                            width: 130,
                                            align: 'center' as const,
                                            render: (_: any, record: any) => {
                                                const s = record.qs?.status;
                                                if (!s || s === 'Not Started') return <Tag>Not Started</Tag>;
                                                if (s === 'Passed') return <Tag color="success">Passed</Tag>;
                                                if (s === 'Failed') return <Tag color="error">Failed</Tag>;
                                                return <Tag color="processing">{s}</Tag>;
                                            },
                                        },
                                        {
                                            title: 'Deadline',
                                            key: 'deadline',
                                            width: 180,
                                            align: 'center' as const,
                                            render: (_: any, record: any) => {
                                                const quizId = record.id;
                                                const override = quizId ? deadlineOverrides[quizId] : undefined;
                                                const original = record.originalDeadline || 0;
                                                const { name: modName } = parseModuleId(record.name);
                                                return (
                                                    <Space direction="vertical" size={2} style={{ alignItems: 'center' }}>
                                                        {override ? (
                                                            <>
                                                                <Tooltip title={`Extended to ${dayjs.unix(override).format('D MMM YY, HH:mm')}`}>
                                                                    <Tag color="orange" style={{ cursor: 'default', margin: 0 }}>
                                                                        {dayjs.unix(override).format('D MMM YY')}
                                                                    </Tag>
                                                                </Tooltip>
                                                                {original > 0 && (
                                                                    <Tooltip title={`Original: ${dayjs.unix(original).format('D MMM YY, HH:mm')}`}>
                                                                        <Text delete type="secondary" style={{ fontSize: 11 }}>
                                                                            {dayjs.unix(original).format('D MMM YY')}
                                                                        </Text>
                                                                    </Tooltip>
                                                                )}
                                                            </>
                                                        ) : original > 0 ? (
                                                            <Tooltip title={dayjs.unix(original).format('D MMM YY, HH:mm')}>
                                                                <Text style={{ fontSize: 12 }}>{dayjs.unix(original).format('D MMM YY')}</Text>
                                                            </Tooltip>
                                                        ) : (
                                                            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
                                                        )}
                                                        {quizId > 0 && !isDeactivated && !deadlinePassed && (
                                                            <Space size={2}>
                                                                <Tooltip title={override ? 'Edit extension' : 'Set deadline extension'}>
                                                                    <Button
                                                                        size="small" type="text" icon={<EditOutlined />}
                                                                        onClick={() => setOverridePicker({ quizId, quizName: modName })}
                                                                    />
                                                                </Tooltip>
                                                                {override && (
                                                                    <Tooltip title="Remove extension">
                                                                        <Button
                                                                            size="small" type="text" danger icon={<DeleteOutlined />}
                                                                            onClick={async () => {
                                                                                await removeDeadlineOverride(trainingId!, quizId, selectedStudent.email);
                                                                                setDeadlineOverrides(prev => { const n = { ...prev }; delete n[quizId]; return n; });
                                                                                message.success('Deadline extension removed');
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Space>
                                                        )}
                                                    </Space>
                                                );
                                            },
                                        },
                                    ];

                                    return (
                                        <>
                                        <Card
                                            style={{ borderRadius: 12 }}
                                            title={
                                                <Space>
                                                    <Avatar icon={<UserOutlined />} src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatDisplayName(studentData.fullname, studentData.email) || 'U')}&background=random`} />
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{formatDisplayName(studentData.fullname, studentData.email)}</div>
                                                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{studentData.email}</div>
                                                    </div>
                                                </Space>
                                            }
                                            extra={<Tag color={statusColor}>{_sps}</Tag>}
                                        >
                                            {nestedHierarchy ? (
                                                <Collapse
                                                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                                                    defaultActiveKey={nestedHierarchy.map((s: any) => s.id)}
                                                    style={{ background: 'transparent', border: 'none' }}
                                                >
                                                    {nestedHierarchy.map((subject: any) => (
                                                        <Collapse.Panel
                                                            key={subject.id}
                                                            header={<Space><FolderOutlined style={{ color: '#fa541c' }} /><Text strong style={{ fontSize: 14 }}>{subject.name}</Text></Space>}
                                                            style={{ background: '#fafafa', marginBottom: 8, borderRadius: 8 }}
                                                        >
                                                            {(subject.chapters || []).map((chapter: any, ci: number) => {
                                                                const rows = (chapter.modules || []).map((hMod: any) => {
                                                                    const quizId = Number(hMod.moodleId) || 0;
                                                                    let qs: any;
                                                                    let resolvedId = quizId;

                                                                    if (quizId) {
                                                                        // Primary: exact quiz ID match.
                                                                        qs = (studentData.quizscores || []).find((q: any) => q.quizid === quizId);
                                                                    } else {
                                                                        // Legacy fallback: no moodleId stored. Resolve by name against
                                                                        // aiProgress.modules to get candidate quiz IDs.
                                                                        const cleanName = parseModuleId(hMod.name).name;
                                                                        const candidateIds = (aiProgress?.modules || [])
                                                                            .filter((m: any) => parseModuleId(m.name).name === cleanName)
                                                                            .map((m: any) => m.id);

                                                                        // Grade: only attempted entries, pick max to avoid bleeding a
                                                                        // score from a same-named quiz the participant did elsewhere.
                                                                        const attempted = (studentData.quizscores || []).filter(
                                                                            (q: any) => candidateIds.includes(q.quizid) && q.status !== 'Not Started'
                                                                        );
                                                                        qs = attempted.reduce((best: any, cur: any) => (!best || cur.grade > best.grade ? cur : best), null) ?? undefined;

                                                                        // Deadline button ID: prefer unique name match (works even before
                                                                        // the participant attempts — admin may want to set deadline early).
                                                                        // If multiple quizzes share the same name, fall back to qs.quizid
                                                                        // (the attempted one we're showing), or 0 if truly ambiguous.
                                                                        resolvedId = candidateIds.length === 1
                                                                            ? candidateIds[0]
                                                                            : (qs?.quizid || 0);
                                                                    }

                                                                    return { _key: hMod.id, id: resolvedId, name: hMod.name, qs, originalDeadline: (hMod as any).deadline || 0 };
                                                                });
                                                                return (
                                                                    <div key={chapter.id} style={{ marginBottom: ci < (subject.chapters || []).length - 1 ? 20 : 0 }}>
                                                                        <Divider orientation="left" plain style={{ margin: '8px 0 12px', fontSize: 12, color: '#8c8c8c' }}>
                                                                            {chapter.name}
                                                                        </Divider>
                                                                        <Table
                                                                            dataSource={rows}
                                                                            rowKey="_key"
                                                                            size="small"
                                                                            pagination={false}
                                                                            expandable={modExpandable}
                                                                            columns={modColumns}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </Collapse.Panel>
                                                    ))}
                                                </Collapse>
                                            ) : (
                                                <Table
                                                    dataSource={(aiProgress?.modules || []).map((mod: any) => ({
                                                        ...mod,
                                                        qs: (studentData.quizscores || []).find((q: any) => q.quizid === mod.id),
                                                    }))}
                                                    rowKey="id"
                                                    size="small"
                                                    pagination={false}
                                                    expandable={modExpandable}
                                                    columns={modColumns}
                                                />
                                            )}
                                        </Card>
                                        </>
                                    );
                                })()}
                            </Col>
                        </Row>
                        </>
                    )}
                </>
            ) : (
            <Tabs
                    defaultActiveKey="modules"
                    style={{ marginTop: '24px' }}
                    items={[
                        {
                            key: 'modules',
                            label: <Space><BookOutlined />Modules</Space>,
                            children: (
                                modules.length === 0 ? <Empty description="No modules available" /> :
                                nestedHierarchy ? (
                                    // ── Nested hierarchy view ──
                                    <Collapse
                                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                                        defaultActiveKey={nestedHierarchy.map(s => s.id)}
                                    >
                                        {nestedHierarchy.map(subject => (
                                            <Collapse.Panel key={subject.id}
                                                header={<Space><FolderOutlined style={{ color: '#fa541c' }} /><Text strong style={{ fontSize: 15 }}>{subject.name}</Text></Space>}
                                            >
                                                <Collapse
                                                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                                                    defaultActiveKey={subject.chapters.map(c => c.id)}
                                                    style={{ background: '#fafafa', borderRadius: 8 }}
                                                >
                                                    {subject.chapters.map(chapter => (
                                                        <Collapse.Panel key={chapter.id}
                                                            header={<Space><BookOutlined style={{ color: '#1677ff' }} /><Text style={{ fontSize: 14 }}>{chapter.name}</Text></Space>}
                                                        >
                                                            <Row gutter={[16, 16]}>
                                                                {chapter.modules.map(hMod => {
                                                                    const module = ((hMod as any).moodleId
                                                                        ? modules.find(m => m.moodleInstance === (hMod as any).moodleId) ?? modules.find(m => m.name === hMod.name)
                                                                        : modules.find(m => m.name === hMod.name));
                                                                    if (!module) return null;
                                                                    const moduleStatus = module.status || TrainingStatus.NOT_STARTED;
                                                                    const modulePassed = moduleStatus === TrainingStatus.PASSED;
                                                                    const isPendingApproval = training?.status === TrainingStatus.REQUESTED;
                                                                    // Use hierarchy JSON as source of truth for attempt limit (Moodle API may not return it)
                                                                    const effectiveAttemptLimit = (hMod as any).attemptLimit ?? module.attemptLimit ?? 0;
                                                                    const attemptLimitReached = effectiveAttemptLimit > 0 && (module.attempts ?? 0) >= effectiveAttemptLimit;
                                                                    const moodleQuizId = (hMod as any).moodleId;
                                                                    const deadline = deadlineOverrides[moodleQuizId] || (hMod as any).deadline || 0;
                                                                    const deadlineDay = deadline > 0 ? dayjs.unix(deadline) : null;
                                                                    const deadlinePast = deadlineDay ? deadlineDay.isBefore(dayjs()) : false;
                                                                    const canStart = active && !modulePassed && !attemptLimitReached && !deadlinePast;
                                                                    const hasAttempted = (module.attempts ?? 0) > 0;
                                                                    const scoreValue = Math.round(module.grade ?? 0);
                                                                    const getStatusTag = (s: any) => {
                                                                        if (s === TrainingStatus.PASSED) return <Tag color="success">PASSED</Tag>;
                                                                        if (s === TrainingStatus.FAILED) return <Tag color="error">FAILED</Tag>;
                                                                        if (s === TrainingStatus.IN_PROGRESS) return <Tag color="processing">IN PROGRESS</Tag>;
                                                                        return <Tag color="default">{s?.toUpperCase() || 'NOT STARTED'}</Tag>;
                                                                    };
                                                                    return (
                                                                        <Col xs={24} md={12} lg={8} key={hMod.id}>
                                                                            <Card hoverable={canStart} style={{ height: '100%', borderRadius: '12px', display: 'flex', flexDirection: 'column', opacity: isDeactivated ? 0.7 : 1 }} bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                                                                                    <Title level={4} style={{ margin: 0, flex: 1, marginRight: 12 }}>{hMod.name}</Title>
                                                                                    {getStatusTag(module.status)}
                                                                                </div>
                                                                                <Space style={{ marginBottom: 10 }} size={16} wrap>
                                                                                    {hasAttempted && (
                                                                                        <Text type="secondary" style={{ fontSize: 12 }}>Score: <Text strong style={{ fontSize: 13 }}>{scoreValue}%</Text></Text>
                                                                                    )}
                                                                                    <Text type={attemptLimitReached ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                                                                        {effectiveAttemptLimit > 0
                                                                                            ? `${module.attempts ?? 0} of ${effectiveAttemptLimit} attempt${effectiveAttemptLimit !== 1 ? 's' : ''} used`
                                                                                            : `${module.attempts ?? 0} attempt${(module.attempts ?? 0) !== 1 ? 's' : ''} · Unlimited`
                                                                                        }
                                                                                    </Text>
                                                                                </Space>
                                                                                {deadlineDay && (
                                                                                    <Space style={{ marginBottom: 8 }}>
                                                                                        <CalendarOutlined style={{ color: deadlinePast && !modulePassed ? '#ff4d4f' : '#1677ff' }} />
                                                                                        <Text type={deadlinePast && !modulePassed ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                                                                            Due: <Text strong style={{ fontSize: 12, color: deadlinePast && !modulePassed ? '#ff4d4f' : undefined }}>{deadlineDay.format('D MMM YY, HH:mm')}</Text>
                                                                                        </Text>
                                                                                    </Space>
                                                                                )}
                                                                                {hMod.description && <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: '13px', minHeight: '40px' }}>{hMod.description}</Paragraph>}
                                                                                <Space style={{ marginBottom: 8 }}>
                                                                                    <ClockCircleOutlined style={{ color: '#fa541c' }} />
                                                                                    <Text strong>{hMod.estimatedTime || 30} Mins</Text>
                                                                                </Space>
                                                                                {hasAttempted && (() => {
                                                                                    const fb = participantFeedback.get(module.moodleInstance ?? 0);
                                                                                    if (!fb) return null;
                                                                                    return (
                                                                                        <Collapse ghost size="small" style={{ marginBottom: 8, borderRadius: 6 }}>
                                                                                            <Collapse.Panel key="fb" header={<Text style={{ fontSize: 12, color: '#722ed1' }}><BulbOutlined /> AI Feedback</Text>}>
                                                                                                <Row gutter={8}>
                                                                                                    <Col span={12}>
                                                                                                        <Text style={{ fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600, color: '#ff4d4f' }}>Improvement Areas</Text>
                                                                                                        {fb.whyfailed.map((b, i) => <div key={i} style={{ fontSize: 11, marginBottom: 2 }}>• {b}</div>)}
                                                                                                    </Col>
                                                                                                    <Col span={12}>
                                                                                                        <Text style={{ fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600, color: '#1677ff' }}>Focus Topics</Text>
                                                                                                        {fb.focusareas.map((b, i) => <div key={i} style={{ fontSize: 11, marginBottom: 2 }}>• {b}</div>)}
                                                                                                    </Col>
                                                                                                </Row>
                                                                                            </Collapse.Panel>
                                                                                        </Collapse>
                                                                                    );
                                                                                })()}
                                                                                <div style={{ marginTop: 'auto' }}>
                                                                                    {isDeactivated || isExpired ? (
                                                                                        <Button block disabled icon={<StopOutlined />}>Closed</Button>
                                                                                    ) : modulePassed ? (
                                                                                        <Button block disabled icon={<CheckCircleOutlined />} style={{ backgroundColor: '#f6ffed', color: '#52c41a' }}>Completed</Button>
                                                                                    ) : attemptLimitReached ? (
                                                                                        <Button block disabled icon={<StopOutlined />}>Attempts Exhausted</Button>
                                                                                    ) : deadlinePast ? (
                                                                                        <Button block disabled icon={<ClockCircleOutlined />}>Deadline Passed</Button>
                                                                                    ) : isPendingApproval ? (
                                                                                        <Button block disabled icon={<ClockCircleOutlined />}>Pending Approval</Button>
                                                                                    ) : (
                                                                                        <Button block type="primary" disabled={!canStart}
                                                                                            style={{ backgroundColor: canStart ? '#fa541c' : undefined }}
                                                                                            onClick={() => handleStartAssessment(module.moduleId, module.moodleInstance ?? 0)}>
                                                                                            {hasAttempted ? 'Retake Assessment' : 'Take Assessment'}
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </Card>
                                                                        </Col>
                                                                    );
                                                                })}
                                                            </Row>
                                                        </Collapse.Panel>
                                                    ))}
                                                </Collapse>
                                            </Collapse.Panel>
                                        ))}
                                    </Collapse>
                                ) : (
                                    // ── Flat card grid (existing programs) ──
                                    <Row gutter={[24, 24]}>
                                        {modules.map(module => {
                                            const moduleStatus = module.status || TrainingStatus.NOT_STARTED;
                                            const modulePassed = moduleStatus === TrainingStatus.PASSED;
                                            const isPendingApproval = training?.status === TrainingStatus.REQUESTED;
                                            const attemptLimitReached = (module.attemptLimit ?? 0) > 0 && (module.attempts ?? 0) >= (module.attemptLimit ?? 0);
                                            const deadlineTs = module.deadline ? parseInt(module.deadline) : 0;
                                            const deadlinePast = deadlineTs > 0 ? dayjs.unix(deadlineTs).isBefore(dayjs()) : false;
                                            const canStart = active && !modulePassed && !attemptLimitReached && !deadlinePast;
                                            const hasAttempted = (module.attempts ?? 0) > 0;
                                            const scoreValue = Math.round(module.grade ?? 0);

                                            const getStatusTag = (s: TrainingStatus | string | undefined) => {
                                                if (s === TrainingStatus.PASSED) return <Tag color="success">PASSED</Tag>;
                                                if (s === TrainingStatus.FAILED) return <Tag color="error">FAILED</Tag>;
                                                if (s === TrainingStatus.IN_PROGRESS) return <Tag color="processing">IN PROGRESS</Tag>;
                                                return <Tag color="default">{s?.toUpperCase() || 'NOT STARTED'}</Tag>;
                                            };

                                            const { name: moduleDisplayName, description: parsedDescription } = parseModuleId(module.name);
                                            const moduleTime = module.estimatedTime || 30;
                                            const moduleDescription = stripHtml(module.description || parsedDescription || '');

                                            return (
                                                <Col xs={24} md={12} lg={8} key={module.moduleId}>
                                                    <Card hoverable={canStart} style={{ height: '100%', borderRadius: '12px', display: 'flex', flexDirection: 'column', opacity: isDeactivated ? 0.7 : 1 }} bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                                                            <Title level={4} style={{ margin: 0, flex: 1, marginRight: 12 }}>{moduleDisplayName}</Title>
                                                            {getStatusTag(module.status)}
                                                        </div>

                                                        <Space style={{ marginBottom: 10 }} size={16} wrap>
                                                            {hasAttempted && (
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    Score: <Text strong style={{ fontSize: 13 }}>{scoreValue}%</Text>
                                                                </Text>
                                                            )}
                                                            <Text type={attemptLimitReached ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                                                {(module.attemptLimit ?? 0) > 0
                                                                    ? `${module.attempts ?? 0} of ${module.attemptLimit} attempt${module.attemptLimit !== 1 ? 's' : ''} used`
                                                                    : `${module.attempts ?? 0} attempt${(module.attempts ?? 0) !== 1 ? 's' : ''} · Unlimited`
                                                                }
                                                            </Text>
                                                        </Space>

                                                        {moduleDescription && (
                                                            <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: '13px', minHeight: '40px' }}>
                                                                {moduleDescription}
                                                            </Paragraph>
                                                        )}

                                                        <Space style={{ marginBottom: 8 }}>
                                                            <ClockCircleOutlined style={{ color: '#fa541c' }} />
                                                            <Text strong>{moduleTime} Mins</Text>
                                                        </Space>

                                                        {hasAttempted && (() => {
                                                            const fb = participantFeedback.get(module.moodleInstance ?? 0);
                                                            if (!fb) return null;
                                                            return (
                                                                <Collapse ghost size="small" style={{ marginBottom: 8, borderRadius: 6 }}>
                                                                    <Collapse.Panel key="fb" header={<Text style={{ fontSize: 12, color: '#722ed1' }}><BulbOutlined /> AI Feedback</Text>}>
                                                                        <Row gutter={8}>
                                                                            <Col span={12}>
                                                                                <Text style={{ fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600, color: '#ff4d4f' }}>Improvement Areas</Text>
                                                                                {fb.whyfailed.map((b, i) => <div key={i} style={{ fontSize: 11, marginBottom: 2 }}>• {b}</div>)}
                                                                            </Col>
                                                                            <Col span={12}>
                                                                                <Text style={{ fontSize: 11, display: 'block', marginBottom: 4, fontWeight: 600, color: '#1677ff' }}>Focus Topics</Text>
                                                                                {fb.focusareas.map((b, i) => <div key={i} style={{ fontSize: 11, marginBottom: 2 }}>• {b}</div>)}
                                                                            </Col>
                                                                        </Row>
                                                                    </Collapse.Panel>
                                                                </Collapse>
                                                            );
                                                        })()}

                                                        <div style={{ marginTop: 'auto' }}>
                                                            {isDeactivated || isExpired ? (
                                                                <Button block disabled icon={<StopOutlined />}>Closed</Button>
                                                            ) : modulePassed ? (
                                                                <Button block disabled icon={<CheckCircleOutlined />} style={{ backgroundColor: '#f6ffed', color: '#52c41a' }}>Completed</Button>
                                                            ) : attemptLimitReached ? (
                                                                <Button block disabled icon={<StopOutlined />}>Attempts Exhausted</Button>
                                                            ) : deadlinePast ? (
                                                                <Button block disabled icon={<ClockCircleOutlined />}>Deadline Passed</Button>
                                                            ) : isPendingApproval ? (
                                                                <Button block disabled icon={<ClockCircleOutlined />}>Pending Approval</Button>
                                                            ) : (
                                                                <Button
                                                                    block
                                                                    type="primary"
                                                                    disabled={!canStart}
                                                                    style={{ backgroundColor: canStart ? '#fa541c' : undefined }}
                                                                    onClick={() => handleStartAssessment(module.moduleId, module.moodleInstance ?? 0)}
                                                                >
                                                                    {hasAttempted ? 'Retake Assessment' : 'Take Assessment'}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                )
                            ),
                        },
        {
                            key: 'leaderboard',
                            label: <Space><TrophyOutlined />Leaderboard</Space>,
                            children: (
                                <Leaderboard
                                    entries={leaderboard}
                                    variant="aiLed"
                                    emptyDescription="No scores yet — complete an assessment to appear on the leaderboard."
                                />
                            ),
                        },
                    ]}
                />
            )}

            <Modal
                open={!!overridePicker}
                title={`Set deadline extension — ${overridePicker?.quizName ?? ''}`}
                okText="Save"
                cancelText="Cancel"
                confirmLoading={overrideSaving}
                okButtonProps={{ disabled: !overrideDate }}
                onCancel={() => { setOverridePicker(null); setOverrideDate(null); }}
                onOk={async () => {
                    if (!overridePicker || !overrideDate || !trainingId || !selectedStudent?.email) return;
                    setOverrideSaving(true);
                    try {
                        const ts = overrideDate.unix();
                        await setDeadlineOverride(trainingId, overridePicker.quizId, selectedStudent.email, ts, selectedStudent.email);
                        setDeadlineOverrides(prev => ({ ...prev, [overridePicker.quizId]: ts }));
                        message.success(`Deadline extended to ${overrideDate.format('D MMM YYYY, HH:mm')}`);
                        setOverridePicker(null);
                        setOverrideDate(null);
                    } catch {
                        message.error('Failed to save deadline extension');
                    } finally {
                        setOverrideSaving(false);
                    }
                }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Pick the new deadline for <Text strong>{selectedStudent?.name}</Text>:</Text>
                    <DatePicker
                        showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }}
                        style={{ width: '100%' }}
                        format="D MMM YYYY HH:mm"
                        disabledDate={d => d.isBefore(dayjs(), 'day')}
                        value={overrideDate}
                        onChange={d => setOverrideDate(d)}
                    />
                    {overridePicker && deadlineOverrides[overridePicker.quizId] && (
                        <Text type="secondary">
                            Current extension: {dayjs.unix(deadlineOverrides[overridePicker.quizId]).format('D MMM YYYY, HH:mm')}
                        </Text>
                    )}
                </Space>
            </Modal>

            <Modal
                title="Share Progress Report"
                open={shareModalOpen}
                onCancel={() => { setShareModalOpen(false); setStakeholderEmails([]); }}
                onOk={handleShareAiReport}
                okText="Send Report"
                confirmLoading={sharing}
                okButtonProps={{ disabled: stakeholderEmails.length === 0 }}
                width="90vw"
                style={{ maxWidth: 1100 }}
            >
                <style>{`.ai-rpt-tbl th{border:1px solid #0050a0!important}.ai-rpt-tbl td{border:1px solid #e0e0e0!important}`}</style>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    <div>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>Send Report To</Typography.Text>
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Type email and press Enter or comma to add"
                            value={stakeholderEmails}
                            onChange={setStakeholderEmails}
                            tokenSeparators={[',', ';']}
                            open={false}
                            suffixIcon={null}
                        />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Each recipient gets this report as an Excel attachment.</Typography.Text>
                    </div>

                    {(() => {
                        const reportCols = getAiReportCols();
                        const participants: any[] = aiProgress?.participants || [];
                        const hasGroups = reportCols.some(c => c.group);

                        // Build group spans for the header row
                        const groupSpans: { label: string; count: number }[] = [];
                        if (hasGroups) {
                            for (const col of reportCols) {
                                const last = groupSpans[groupSpans.length - 1];
                                if (last && last.label === col.group) {
                                    last.count++;
                                } else {
                                    groupSpans.push({ label: col.group, count: 1 });
                                }
                            }
                        }

                        return (
                            <div>
                                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    Report Preview &nbsp;
                                    <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                                        ({participants.length} participants · this is what will be emailed as Excel)
                                    </Typography.Text>
                                </Typography.Text>
                                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 420, border: '1px solid #e8e8e8', borderRadius: 6 }}>
                                    <table className="ai-rpt-tbl" style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
                                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                            {hasGroups && (
                                                <tr>
                                                    <th rowSpan={2} style={{ padding: '8px 12px', background: '#001529', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 160, borderRight: '1px solid #0050a0' }}>Name</th>
                                                    <th rowSpan={2} style={{ padding: '8px 12px', background: '#001529', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 180, borderRight: '1px solid #0050a0' }}>Email</th>
                                                    {groupSpans.map((g, i) => (
                                                        <th key={i} colSpan={g.count} style={{ padding: '6px 8px', background: '#002140', color: '#91caff', textAlign: 'center', borderBottom: '1px solid #0050a0', borderRight: '1px solid #0050a0', whiteSpace: 'nowrap' }}>
                                                            {g.label}
                                                        </th>
                                                    ))}
                                                    <th rowSpan={2} style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 90, borderLeft: '1px solid #0050a0' }}>Avg Score</th>
                                                    <th rowSpan={2} style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 100 }}>Status</th>
                                                </tr>
                                            )}
                                            <tr>
                                                {!hasGroups && (
                                                    <>
                                                        <th style={{ padding: '8px 12px', background: '#001529', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 160, borderRight: '1px solid #0050a0' }}>Name</th>
                                                        <th style={{ padding: '8px 12px', background: '#001529', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 180, borderRight: '1px solid #0050a0' }}>Email</th>
                                                    </>
                                                )}
                                                {reportCols.map((col, i) => (
                                                    <th key={i} style={{ padding: '6px 8px', background: '#003a6e', color: '#e6f4ff', textAlign: 'center', borderTop: '1px solid #0050a0', borderRight: '1px solid #0050a0', maxWidth: 140, whiteSpace: 'normal', lineHeight: 1.3 }}>
                                                        {col.name}
                                                    </th>
                                                ))}
                                                {!hasGroups && (
                                                    <>
                                                        <th style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 90, borderLeft: '1px solid #0050a0' }}>Avg Score</th>
                                                        <th style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 100 }}>Status</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {participants.map((p: any, pi: number) => {
                                                const scoreByQuizId: Record<number, any> = {};
                                                (p.quizscores || []).forEach((qs: any) => { scoreByQuizId[qs.quizid] = qs; });
                                                const avgScore = computeParticipantAvgScore(p);
                                                const avgColor = avgScore >= 75 ? HEX.passedText : avgScore >= 50 ? HEX.warnText : avgScore >= 0 ? HEX.failedText : HEX.notStartedText;
                                                const _status = computeParticipantStatus(p);
                                                const statusColor = _status === 'Passed' ? HEX.passedText : _status === 'Failed' ? HEX.failedText : _status === 'In Progress' ? HEX.warnText : _status === 'Incomplete' ? HEX.warnText : HEX.notStartedText;
                                                return (
                                                    <tr key={p.email} style={{ background: pi % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', whiteSpace: 'nowrap', fontWeight: 500 }}>
                                                            {formatDisplayName(p.fullname, p.email) || 'Unknown'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', color: '#595959', whiteSpace: 'nowrap' }}>{p.email}</td>
                                                        {reportCols.map((col, ci) => {
                                                            const qs = col.quizId ? scoreByQuizId[col.quizId] : null;
                                                            if (!qs || qs.status === 'Not Started') return (
                                                                <td key={ci} style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', color: '#d9d9d9' }}>—</td>
                                                            );
                                                            const bg = qs.status === 'Passed' ? HEX.passedBg : HEX.failedBg;
                                                            const fg = qs.status === 'Passed' ? HEX.passedText : HEX.failedText;
                                                            return (
                                                                <td key={ci} style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', background: bg }}>
                                                                    <div style={{ color: fg, fontWeight: 600, fontSize: 11 }}>{qs.status}</div>
                                                                    <div style={{ color: fg, fontSize: 10 }}>{Math.round(qs.grade)}%</div>
                                                                    <div style={{ color: HEX.secondaryText, fontSize: 10 }}>{qs.attempts} att</div>
                                                                </td>
                                                            );
                                                        })}
                                                        <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderLeft: '1px solid #f0f0f0', fontWeight: 700, color: avgColor }}>
                                                            {avgScore >= 0 ? `${avgScore.toFixed(1)}%` : '—'}
                                                        </td>
                                                        <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', fontWeight: 600, color: statusColor }}>
                                                            {_status}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}
                </Space>
            </Modal>
        </div>
    );
};

export default AILedTrainingDetails;
